import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi, type ContactBody } from './api';
import { keys } from '@/lib/query/keys';
import { useApiError } from '@/lib/query/useApiError';
import { DataView } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Button } from '@/lib/ui/Button';
import { Dialog } from '@/lib/ui/Dialog';
import { ConfirmDialog } from '@/lib/ui/ConfirmDialog';
import { TextInput } from '@/lib/ui/Field';
import { Switch } from '@/lib/ui/Switch';
import { MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { I18nField } from '@/lib/forms/widgets/I18nField';
import { useToast } from '@/lib/ui';
import type { ContactDirectoryEntry, I18n } from '@/lib/api/types';

const EMPTY_LABEL: I18n = { en: '', ar: '' };

interface Draft {
  isNew: boolean;
  key: string;
  label: I18n;
  email: string;
  phone: string;
  sla: string;
  is_visible: boolean;
  sort_order: number;
}

function blankDraft(): Draft {
  return {
    isNew: true,
    key: '',
    label: EMPTY_LABEL,
    email: '',
    phone: '',
    sla: '',
    is_visible: true,
    sort_order: 0,
  };
}

/** Contact directory tab — department contacts shown on the public site (spec §15.2). */
export function ContactDirectoryTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();

  const query = useQuery({
    queryKey: keys.settings.contactDirectory(),
    queryFn: () => settingsApi.listContactDirectory(),
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toDelete, setToDelete] = useState<ContactDirectoryEntry | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: keys.settings.contactDirectory() });

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body: ContactBody = {
        label: d.label,
        email: d.email.trim(),
        phone: d.phone.trim() || undefined,
        sla: d.sla.trim() || undefined,
        is_visible: d.is_visible,
        sort_order: d.sort_order,
      };
      return settingsApi.putContact(d.key.trim(), body);
    },
    onSuccess: () => {
      void invalidate();
      toast.success(t('settings.contact.savedTitle'), t('settings.contact.savedDetail'));
      setDraft(null);
    },
    onError: (e) => onError(e, { onValidation: setErrors }),
  });

  const remove = useMutation({
    mutationFn: (entry: ContactDirectoryEntry) => settingsApi.deleteContact(entry.key),
    onSuccess: () => {
      void invalidate();
      toast.success(t('settings.contact.deleted'));
      setToDelete(null);
    },
    onError: (e) => onError(e),
  });

  const submit = () => {
    if (!draft) return;
    const next: Record<string, string> = {};
    if (!draft.key.trim()) next.key = t('settings.contact.errKeyRequired');
    if (!draft.label.en.trim()) next['label.en'] = t('settings.contact.errLabelRequired');
    if (!draft.email.trim()) next.email = t('settings.contact.errEmailRequired');
    setErrors(next);
    if (Object.keys(next).length) return;
    save.mutate(draft);
  };

  const ordered = useMemo(
    () => (query.data ? [...query.data].sort((a, b) => a.sort_order - b.sort_order) : []),
    [query.data],
  );

  const columns: Column<ContactDirectoryEntry>[] = useMemo(
    () => [
      { id: 'key', header: t('common.key'), cell: (r) => <MonoValue>{r.key}</MonoValue> },
      {
        id: 'label',
        header: t('settings.contact.colLabel'),
        cell: (r) => <span className="text-ink-deep">{r.label.en || '—'}</span>,
      },
      { id: 'email', header: t('settings.contact.colEmail'), cell: (r) => <MonoValue>{r.email}</MonoValue> },
      { id: 'phone', header: t('settings.contact.colPhone'), cell: (r) => <MonoValue>{r.phone || '—'}</MonoValue> },
      {
        id: 'visible',
        header: t('settings.contact.colVisible'),
        cell: (r) =>
          r.is_visible ? <Tag variant="teal">{t('settings.contact.visibleTag')}</Tag> : <Tag variant="amber">{t('settings.contact.hiddenTag')}</Tag>,
      },
      {
        id: 'sort',
        header: t('settings.contact.colOrder'),
        align: 'end',
        cell: (r) => <MonoValue>{r.sort_order}</MonoValue>,
      },
      {
        id: 'actions',
        header: '',
        align: 'end',
        cell: (r) => (
          <div className="flex justify-end gap-xs" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setErrors({});
                setDraft({
                  isNew: false,
                  key: r.key,
                  label: r.label,
                  email: r.email,
                  phone: r.phone,
                  sla: r.sla,
                  is_visible: r.is_visible,
                  sort_order: r.sort_order,
                });
              }}
            >
              {t('common.edit')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setToDelete(r)}>
              {t('common.delete')}
            </Button>
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <div>
      <div className="mb-md flex justify-end">
        <Button
          onClick={() => {
            setErrors({});
            setDraft({ ...blankDraft(), sort_order: ordered.length });
          }}
        >
          {t('settings.contact.newContact')}
        </Button>
      </div>

      <DataView query={query} isEmpty={(d) => d.length === 0}>
        {() => <DataTable columns={columns} rows={ordered} rowKey={(r) => r.key} />}
      </DataView>

      <Dialog
        open={!!draft}
        onOpenChange={(o) => !o && setDraft(null)}
        title={draft && !draft.isNew ? t('settings.contact.editContact') : t('settings.contact.newContact')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
            <Button loading={save.isPending} onClick={submit}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="flex flex-col gap-md">
            <TextInput
              label={t('common.key')}
              required
              value={draft.key}
              disabled={!draft.isNew}
              onChange={(e) => setDraft({ ...draft, key: e.target.value })}
              error={errors.key}
            />
            <I18nField
              label={t('settings.contact.fieldLabel')}
              value={draft.label}
              onChange={(label) => setDraft({ ...draft, label })}
              required
              error={errors['label.en']}
            />
            <TextInput
              label={t('settings.contact.email')}
              type="email"
              required
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              error={errors.email}
            />
            <TextInput
              label={t('settings.contact.phone')}
              value={draft.phone}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            />
            <TextInput
              label={t('settings.contact.sla')}
              value={draft.sla}
              onChange={(e) => setDraft({ ...draft, sla: e.target.value })}
              hint={t('settings.contact.slaHint')}
            />
            <TextInput
              type="number"
              label={t('settings.contact.sortOrder')}
              value={String(draft.sort_order)}
              onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) || 0 })}
            />
            <Switch
              label={t('settings.contact.visible')}
              checked={draft.is_visible}
              onCheckedChange={(v) => setDraft({ ...draft, is_visible: v })}
            />
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={t('settings.contact.deleteTitle')}
        description={toDelete ? t('settings.contact.deleteDescription', { key: toDelete.key }) : undefined}
        confirmLabel={t('common.delete')}
        tone="danger"
        loading={remove.isPending}
        onConfirm={() => toDelete && remove.mutate(toDelete)}
      />
    </div>
  );
}
